import { ConferenceOptions } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import {
    addCallContextParameter,
    getConferenceRecordingConfiguration,
    getReservationsByTask,
    isCallTask,
    sendTrackingEvent
} from "../../ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { Action, TaskReservation } from "~/modules/actions/Action";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { getConfigRegion } from "~/utils/regionUtil";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";




export interface AcceptTaskOptions {
    conferenceOptions?: ConferenceOptions;
}



















export class AcceptTask implements Action<Promise<TaskReservation>> {
    


    #taskSid: string;

    


    #acceptTaskOptions?: AcceptTaskOptions;

    


    #defaultConferenceOptions: ConferenceOptions = {
        endConferenceOnCustomerExit: true,
        endConferenceOnExit: true
    };

    constructor(taskSid: string, acceptTaskOptions?: AcceptTaskOptions) {
        this.#taskSid = taskSid;
        if (acceptTaskOptions) {
            this.#acceptTaskOptions = {
                ...acceptTaskOptions,
                conferenceOptions: {
                    ...this.#defaultConferenceOptions,
                    ...acceptTaskOptions.conferenceOptions
                }
            };
        } else {
            this.#acceptTaskOptions = {
                conferenceOptions: this.#defaultConferenceOptions
            };
        }
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const session = context.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const flexSessionIdentifier = session.flexSessionIdentifier;
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );
        const clientOptions = context.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        await cbm.init();

        logger.debug(`acceptTask invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;

        


        const conferenceOptions = this.#acceptTaskOptions?.conferenceOptions || {};

        if (!worker) {
            const errorMsg = "acceptTask: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "pending");

        if (!reservation) {
            const errorMsg = `acceptTask: Reservation for task ${this.#taskSid} not found`;

            sendTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid
            });

            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!isCallTask(reservation.task)) {
            try {
                const acceptedReservation = await cbm.acceptReservation(reservation);
                sendTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid
                });
                return { task: acceptedReservation?.task, reservation: acceptedReservation };
            } catch (error) {
                const errorMsg = `acceptTask: Failed to accept reservation ${reservation.sid}`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid
                });
                return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            }
        }
        try {
            const to = addCallContextParameter(
                `flex_session_identifier=${flexSessionIdentifier}`,
                taskRouter.worker.attributes.contact_uri,
                this.#acceptTaskOptions?.conferenceOptions?.to
            );

            const effectiveConferenceOptions = {
                ...conferenceOptions,
                to,
                ...getConferenceRecordingConfiguration(
                    accountConfig.get(),
                    getConfigRegion(clientOptions),
                    reservation.task.sid,
                    conferenceOptions
                )
            };

            await reservation.conference(effectiveConferenceOptions);

            sendTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: reservation.sid
            });

            return { task: reservation.task, reservation };
        } catch (e) {
            const errorMsg = `acceptTask: Failed to create conference for ${reservation.sid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.AcceptTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
